import { DetailedRegistrationData } from '@sportninja/common/types/Registration'

/**
 * Preview mode enum for toggling between desktop and mobile preview
 */
export type PreviewMode = 'desktop' | 'mobile'

/**
 * State interface for RegistrationPreview component
 */
export interface RegistrationPreviewState {
  /** Registration data from API */
  registration: DetailedRegistrationData | null
  /** Loading state for registration fetch */
  loading: boolean
  /** Error state for registration fetch */
  error: Error | null
  /** Current window width */
  windowWidth: number
  /** Current window height */
  windowHeight: number
  /** Whether the device is desktop (width > 768) */
  isDesktop: boolean
  /** Resolved background image URL */
  backgroundImage: string
  /** Current preview mode */
  previewMode: PreviewMode
}

/**
 * Action types for the RegistrationPreview reducer
 */
export type RegistrationPreviewAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: DetailedRegistrationData }
  | { type: 'FETCH_ERROR'; payload: Error }
  | { type: 'WINDOW_RESIZE'; payload: { width: number; height: number } }
  | { type: 'SET_BACKGROUND_IMAGE'; payload: string }
  | { type: 'TOGGLE_PREVIEW_MODE' }

/**
 * Initial state for the RegistrationPreview reducer
 */
export const initialState: RegistrationPreviewState = {
  registration: null,
  loading: true,
  error: null,
  windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
  windowHeight: typeof window !== 'undefined' ? window.innerHeight : 768,
  isDesktop: typeof window !== 'undefined' ? window.innerWidth > 768 : true,
  backgroundImage: '/images/game_hockey.png',
  previewMode: 'desktop',
}

/**
 * Reducer function for RegistrationPreview state management
 * @param state - Current state
 * @param action - Action to perform
 * @returns Updated state
 *
 * Handles the following actions:
 * - FETCH_START: Sets loading to true, clears error
 * - FETCH_SUCCESS: Sets registration data, clears loading and error
 * - FETCH_ERROR: Sets error, clears loading
 * - WINDOW_RESIZE: Updates window dimensions and isDesktop flag
 * - SET_BACKGROUND_IMAGE: Updates the background image URL
 * - TOGGLE_PREVIEW_MODE: Toggles between desktop and mobile preview modes
 */
export const registrationPreviewReducer = (
  state: RegistrationPreviewState,
  action: RegistrationPreviewAction
): RegistrationPreviewState => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        loading: true,
        error: null,
      }

    case 'FETCH_SUCCESS':
      return {
        ...state,
        registration: action.payload,
        loading: false,
        error: null,
      }

    case 'FETCH_ERROR':
      return {
        ...state,
        registration: null,
        loading: false,
        error: action.payload,
      }

    case 'WINDOW_RESIZE':
      return {
        ...state,
        windowWidth: action.payload.width,
        windowHeight: action.payload.height,
        isDesktop: action.payload.width > 768,
      }

    case 'SET_BACKGROUND_IMAGE':
      return {
        ...state,
        backgroundImage: action.payload,
      }

    case 'TOGGLE_PREVIEW_MODE': {
      const newPreviewMode: PreviewMode =
        state.previewMode === 'desktop' ? 'mobile' : 'desktop'
      const newDimensions =
        newPreviewMode === 'mobile'
          ? { width: 375, height: 812 }
          : {
              width: typeof window !== 'undefined' ? window.innerWidth : 1024,
              height: typeof window !== 'undefined' ? window.innerHeight : 768,
            }

      return {
        ...state,
        previewMode: newPreviewMode,
        windowWidth: newDimensions.width,
        windowHeight: newDimensions.height,
        isDesktop: newDimensions.width > 768,
      }
    }

    default:
      return state
  }
}
